import { useApolloClient } from "@apollo/client";
import { compact } from "lodash";
import { useState } from "react";
import {
  AssessmentGroupDelivery,
  AssessmentNominationFragment,
  ComplianceProgramParticipantStatus,
  GetComplianceProgramProgressQuery,
} from "types/graphql-schema";

import AssessmenNominationtDialog from "@apps/assessments/components/peer-nomination/nomination-dialog";
import AppPopover from "@components/popover/app-popover";
import StatusPill, {
  StatusPillEnum,
} from "@components/status-pill/status-pill";
import { Tooltip } from "@components/tooltip/tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";

import getComplianceProgramProgressQuery from "../graphql/get-compliance-program-progress-query";
import {
  getBlockedStepLabel,
  getBlockedStepText,
  getStepProgress,
} from "./compliance-program-progress-constants";
import {
  ComplianceProgramReportingEntityTableCol,
  ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
} from "./compliance-program-progress-entity-table";
import ProgramProgressPillPopoverPanel from "./program-progress-pill-popover-panel";

const ProgramProgressPill = ({
  className,
  status,
  col,
  complianceProgram,
  participantStatus,
}: {
  col: ComplianceProgramReportingEntityTableCol;
  participantStatus: ComplianceProgramReportingEntityTableParticipantStatusWithNominations;
  className?: string;
  status: ComplianceProgramParticipantStatus;
  complianceProgram: NonNullable<
    GetComplianceProgramProgressQuery["complianceProgram"]
  >;
}) => {
  const client = useApolloClient();
  const step = getStepProgress(participantStatus, col.keyName);
  // The nomination dialog opened from the popover's "Nominate peers" link. It lives here,
  // outside the popover, because the popover closes as soon as the dialog takes focus.
  const [assessmentNomination, setAssessmentNomination] =
    useState<AssessmentNominationFragment | null>(null);

  // Reload the table after a save, so a cell that waited for nominations stops saying
  // "Awaiting Nominations" once the person has a nominee. Not on Close: nothing changed,
  // and this query is heavy. A failed reload leaves the table as it was.
  const handleSavedNominations = () => {
    client
      .refetchQueries({ include: [getComplianceProgramProgressQuery] })
      .catch(() => {});
  };

  const pillStatus =
    status === ComplianceProgramParticipantStatus.NotApplicable
      ? StatusPillEnum.NotApplicable
      : status === ComplianceProgramParticipantStatus.NotStarted
        ? StatusPillEnum.NotApplicable
        : status === ComplianceProgramParticipantStatus.InProgress
          ? StatusPillEnum.InProgress
          : StatusPillEnum.Complete;

  const statusLabel =
    status === ComplianceProgramParticipantStatus.NotStarted
      ? col.keyName === "oneononeStatus"
        ? "Not Scheduled"
        : col.keyName === "calibrationStatus"
          ? "Not Calibrated"
          : col.keyName === "deliveryStatus"
            ? compact([
                complianceProgram.performanceAssessmentTemplate,
                complianceProgram.managerAssessmentTemplate,
                complianceProgram.peerAssessmentTemplate,
              ]).some(
                (template) =>
                  template.delivery === AssessmentGroupDelivery.AdminApproval,
              )
              ? "Not Approved"
              : "Not Shared"
            : "Not Started"
      : status === ComplianceProgramParticipantStatus.InProgress
        ? col.keyName === "deliveryStatus"
          ? "Approved"
          : "In Progress"
        : status === ComplianceProgramParticipantStatus.Complete
          ? col.keyName === "deliveryStatus"
            ? "Shared"
            : col.keyName === "calibrationStatus"
              ? "Calibrated"
              : "Complete"
          : "N/A";

  // A step nobody may start yet is Blocked, and its pill names the wait: "Awaiting
  // Calibration" or "Awaiting Nominations". A step that is simply no longer required keeps
  // "N/A". One is a wait somebody can end, the other is finished business, and calling
  // both N/A hid the difference. The pill names the wait rather than saying "Blocked",
  // because "Blocked" alone left the reader asking what for.
  //
  // The reason for either is in the popover, not in a tooltip: every cell here opens a
  // popover on click, and a tooltip on top of it said the same thing twice.
  //
  // That is also why only one of them is a pill. A wait is a state the column is in,
  // like Not Started or Complete, so it is drawn like them. "N/A" is the absence of a
  // state, and plain text says that better than a pill would.
  //
  // A Blocked cell also says who can end the wait, because "Awaiting pre-calibration"
  // alone left a manager asking us how to unblock their reports.
  const blockedReason = step?.blocked
    ? getBlockedStepText(step, participantStatus.user.name)
    : step?.blockedReason;
  // The reason is on the button, after the word it shows, so a screen reader hears
  // "Awaiting Calibration. Waiting for pre-calibration..." without opening the popover.
  const notApplicableName =
    status === ComplianceProgramParticipantStatus.NotApplicable && blockedReason
      ? `${step?.blocked ? getBlockedStepLabel(step) : statusLabel}. ${blockedReason}`
      : undefined;

  return (
    // gap-1 and a 14px timer, not more: below the 2xl breakpoint a status cell is 104px
    // wide inside its padding, and the longest label that can carry a timer - "Not
    // Scheduled", on the 1-on-1 column - fills exactly that at these sizes. At gap-2 with
    // a 16px icon it needed 109px and pushed into the next column.
    <div className="flex gap-1 items-center">
      <AppPopover
        content={({ open, close }) =>
          open && (
            <ProgramProgressPillPopoverPanel
              participantStatus={participantStatus}
              status={status}
              col={col}
              complianceProgram={complianceProgram}
              onOpenNominations={(nomination) => {
                close();
                setAssessmentNomination(nomination);
              }}
            />
          )
        }
      >
        <AppPopover.Button
          className="outline-hidden focus:outline-hidden focus:ring-0"
          aria-label={notApplicableName}
        >
          {status === ComplianceProgramParticipantStatus.NotApplicable ? (
            step?.blocked ? (
              <StatusPill
                status={StatusPillEnum.NotApplicable}
                label={getBlockedStepLabel(step)}
                className={className}
              />
            ) : (
              <div className="text-gray-500 text-sm text-left">
                {statusLabel}
              </div>
            )
          ) : (
            <StatusPill
              status={pillStatus}
              label={statusLabel}
              className={className}
            />
          )}
        </AppPopover.Button>
      </AppPopover>
      {/* Lateness belongs to the step, so it is marked on the cell that is late. A timer
          beside the pill rather than a rose pill: on a review past its due date most of
          the column is late, and recolouring every cell turned the table into a wall of
          red. The same timer sits in the count at the top of the tab, so the two read as
          one thing. */}
      {step?.overdue && (
        <Tooltip text="Overdue">
          <span aria-label="Overdue">
            <TopicflowIcon
              type="overdue"
              className="h-3.5 w-3.5 text-rose-600 shrink-0"
            />
          </span>
        </Tooltip>
      )}
      {assessmentNomination && (
        <AssessmenNominationtDialog
          assessmentNomination={assessmentNomination}
          onClose={() => setAssessmentNomination(null)}
          onSaved={handleSavedNominations}
        />
      )}
    </div>
  );
};

export default ProgramProgressPill;
